import Firebase from 'EnoticeFirebase';
import { getLogger } from 'utils/logger';
import {
  ApiEndpoint,
  ApiGetEndpoint,
  HEADER_SESSION_ID_KEY
} from 'lib/types/api';
import {
  ApiResponseError,
  isResponseOrError,
  wrapApiError
} from 'lib/types/responses';
import { ERequestTypes, EResponseTypes } from 'lib/types';
import { getBackendUrl } from 'lib/constants';
import { getTenant } from 'lib/api';
import { getBooleanFlag, setCustomLDContext } from 'utils/flags';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { logAndCaptureMessage } from 'utils';
import { ColumnService } from 'lib/services/directory';

class Api {
  async getRestUrl(path: string, forceOldApi = false) {
    const route = path.includes('/') ? path.split('/')[0] : path;

    await setCustomLDContext({
      route
    });

    const useCloudRunApi = forceOldApi
      ? false
      : getBooleanFlag(LaunchDarklyFlags.USE_CLOUD_RUN_API, false);
    return `${getBackendUrl(useCloudRunApi, getTenant())}${path}`;
  }

  private async getUserInfo() {
    try {
      const userAuth = Firebase.auth().currentUser;

      return {
        email: userAuth?.email || 'unknown',
        uid: userAuth?.uid || 'unknown'
      };
    } catch (error) {
      return {
        email: 'unknown',
        uid: 'unknown'
      };
    }
  }

  /**
   * Check if an error is a network failure (e.g., "Failed to fetch")
   * Network failures should be retried, but HTTP errors (4xx, 5xx) should not
   */
  private isNetworkError(error: Error | unknown): boolean {
    if (!(error instanceof Error)) {
      return false;
    }

    const errorMessage = error.message.toLowerCase();
    return (
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('networkerror') ||
      errorMessage.includes('network request failed') ||
      errorMessage.includes('load failed') ||
      (error instanceof TypeError && errorMessage.includes('fetch'))
    );
  }

  private async logApiFallback(
    path: string,
    error: Error,
    statusCode?: number
  ) {
    const userInfo = await this.getUserInfo();
    const errorDetails: Record<string, string> = {
      service: ColumnService.FRONTEND_ROUTING,
      path,
      userEmail: userInfo.email,
      userId: userInfo.uid,
      error: error.message,
      fallbackReason: 'cloud_run_api_failed',
      networkError: 'true'
    };

    if (statusCode) {
      errorDetails.statusCode = statusCode.toString();
    }

    // Try to get IP address from error if available (network errors may contain this)
    // Note: Browser security prevents direct IP access, but we can log what we have
    logAndCaptureMessage(
      `API call failed with Cloud Run URL, falling back to old API URL`,
      errorDetails
    );
  }

  /**
   * When calling our API we send a Firebase ID token which has a 1-hour lifetime.
   * The Firebase Auth SDK automatically refreshes tokens when they're within 5m of
   * expiring and makes sure tokens returned from getIdTokenI() have at least 30s
   * remaining.
   *
   * However if the client and server have significant clock skew then the Firebase
   * Auth SDK can fail to detect that the token is actually expired and the server
   * will reject it.
   */
  private checkForClockSkew(resp: Response) {
    const dateHeader = resp?.headers?.get('date');
    if (!dateHeader) {
      return;
    }

    const serverTime = new Date(dateHeader).getTime();
    if (isNaN(serverTime)) {
      return;
    }

    const clientTime = new Date().getTime();
    const diff = clientTime - serverTime;
    if (Math.abs(diff) > 60000) {
      console.error(
        `Client time and server time differ by more than 1m (${diff}ms)`
      );
    }
  }

  private async getToken() {
    const userAuth = Firebase.auth().currentUser;
    if (!userAuth) throw new Error('No userauth set');

    // If the ID token has less than 5 minutes of validity remaining, we
    // forcibly refresh it. This should normally be hanled by the Firebase
    // SDK but some users were still getting expired tokens.
    const idTokenRes = await userAuth.getIdTokenResult();
    const expirationDate = new Date(idTokenRes.expirationTime);
    const timeToExpiry = expirationDate.getTime() - new Date().getTime();
    if (timeToExpiry < 5 * 60 * 1000) {
      return await userAuth.getIdToken(true);
    }

    return idTokenRes.token;
  }

  async getCommonHeaders() {
    const headers: Record<string, string> = {};
    try {
      const token = await this.getToken();
      headers.Authorization = `Bearer ${token}`;
    } catch (err) {}

    const sessionId = getLogger().getSessionId();
    if (sessionId) {
      headers[HEADER_SESSION_ID_KEY] = sessionId;
    }

    return headers;
  }

  async get(
    path: string,
    params?: Record<string, any>,
    headersOverride?: Record<string, string>,
    isRetry = false
  ): Promise<any> {
    const headers = await this.getCommonHeaders();

    let apiUrl = await this.getRestUrl(path, isRetry);
    const useCloudRunApi = getBooleanFlag(
      LaunchDarklyFlags.USE_CLOUD_RUN_API,
      false
    );

    if (params) {
      const searchParams = new URLSearchParams({
        ...params
      });
      apiUrl += `?${searchParams.toString()}`;
    }

    try {
      const resp = await fetch(apiUrl, {
        headers: { ...headers, ...headersOverride }
      });

      this.checkForClockSkew(resp);
      if (resp.status !== 200) {
        const error = new Error(`${resp.status} error: ${await resp.text()}`);
        // Don't retry HTTP errors (4xx, 5xx) - only retry network failures
        throw error;
      }

      return await resp.json();
    } catch (error) {
      // Only retry network failures (e.g., "Failed to fetch"), not HTTP errors
      if (useCloudRunApi && !isRetry && this.isNetworkError(error)) {
        await this.logApiFallback(path, error as Error);
        return this.get(path, params, headersOverride, true);
      }

      throw error;
    }
  }

  /**
   * @deprecated
   *
   * use api.safePost() instead
   */
  async post(
    path: string,
    data?: Record<string, any>,
    isRetry = false
  ): Promise<any> {
    const headers = await this.getCommonHeaders();
    headers['Content-Type'] = 'application/json';

    const url = await this.getRestUrl(path, isRetry);
    const useCloudRunApi = getBooleanFlag(
      LaunchDarklyFlags.USE_CLOUD_RUN_API,
      false
    );

    try {
      const resp = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(data || {}),
        headers
      });
      this.checkForClockSkew(resp);
      const json = await resp.json();

      if (resp.status !== 200) {
        const error = new Error(
          `${resp.status} error: ${JSON.stringify(json)}`
        );

        // Don't retry HTTP errors (4xx, 5xx) - only retry network failures
        if (isResponseOrError(json)) {
          return json;
        }
        throw error;
      }

      return json;
    } catch (error) {
      // Only retry network failures (e.g., "Failed to fetch"), not HTTP errors
      if (useCloudRunApi && !isRetry && this.isNetworkError(error)) {
        await this.logApiFallback(path, error as Error);
        return this.post(path, data, true);
      }

      throw error;
    }
  }

  /**
   * This function wraps the post function in a safe manner
   * In case of an error response from the code, it will also extract the nested
   * error message from the ApiResponseOrError type and rewrap it for FE
   * use.
   */
  async safePost<T extends ApiEndpoint>(
    path: T,
    data?: ERequestTypes[T]
  ): Promise<EResponseTypes[T] | ApiResponseError> {
    try {
      const result = await this.post(path, data);
      // For ApiResponseOrError, this is already in the format of { response, error }
      return result;
    } catch (error) {
      let errorMessage = (error as Error).message;

      try {
        const errorResponse = JSON.parse(errorMessage.split('error: ')[1]);
        if (errorResponse.error) {
          errorMessage = errorResponse.error;
          return wrapApiError(errorMessage);
        }
        return wrapApiError('Unknown error');
      } catch (e) {
        return wrapApiError(errorMessage);
      }
    }
  }

  /**
   * This function wraps the post function in a safe manner
   * In case of an error response from the code, it will also extract the nested
   * error message from the ApiResponseOrError type and rewrap it for FE
   * use.
   */
  async safePostWithParams<T extends ApiEndpoint>(
    path: string,
    data?: ERequestTypes[T]
  ): Promise<EResponseTypes[T] | ApiResponseError> {
    const res = await this.safePost(path as T, data);
    return res;
  }

  async safeGet<T extends ApiGetEndpoint>(
    path: T,
    params?: Record<string, any>,
    headersOverride?: Record<string, string>
  ): Promise<EResponseTypes[T] | ApiResponseError> {
    try {
      const result = await this.get(path as string, params, headersOverride);
      // For ApiResponseOrError, this is already in the format of { response, error }
      return result;
    } catch (error) {
      let errorMessage = (error as Error).message;

      try {
        const errorResponse = JSON.parse(errorMessage.split('error: ')[1]);
        if (errorResponse.error) {
          errorMessage = errorResponse.error;
          return wrapApiError(errorMessage);
        }
        return wrapApiError('Unknown error');
      } catch (e) {
        return wrapApiError(errorMessage);
      }
    }
  }

  async safeGetWithParams<T extends ApiGetEndpoint>(
    path: string,
    params?: Record<string, any>,
    headersOverride?: Record<string, string>
  ): Promise<EResponseTypes[T] | ApiResponseError> {
    const res = await this.safeGet(path as T, params, headersOverride);
    return res;
  }
}

export default new Api();
